import '~/components/dynamic_search/dynamic_search.scss'

import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Select from 'react-select'
import DateInput from '~/components/dynamic_search/components/DateInput'
import DateTimeInput from '~/components/dynamic_search/components/DateTimeInput'

export default function DynamicSearch({ fields, onChange, exportOptions = {}, sortOptions = {}, pageOptions = {} }) {
  // Memoize the default values to prevent infinite re-renders
  // biome-ignore lint/correctness/useExhaustiveDependencies: todo: re-evaluate dependencies
  const initialSorting = useMemo(() => sortOptions, [sortOptions.sort, sortOptions.dir])
  // biome-ignore lint/correctness/useExhaustiveDependencies: todo: re-evaluate dependencies
  const initialPaging = useMemo(() => pageOptions, [pageOptions.page])
  const initialQuery = useMemo(() => {
    const q = fields.reduce((data, field) => {
      const fieldValue = field?.default || field.value
      // biome-ignore lint/performance/noAccumulatingSpread: todo: fix this
      return fieldValue ? { ...data, [field.name]: fieldValue } : data
    }, {})
    return q
  }, [fields])

  const [query, setQuery] = useState(() => initialQuery)
  const [sort, setSort] = useState(() => {
    return { sort: initialSorting.sort || null, dir: initialSorting.dir || 'asc' }
  })
  const [page, setPage] = useState(() => {
    return initialPaging.page || 1
  })

  const [fieldSelectorOptions] = useState(() => {
    return fields.map((field) => ({ value: field.name, label: field.label }))
  })
  const [fieldSelectorFocus, setFieldSelectorFocus] = useState(false)
  const [currentField, setCurrentField] = useState(null)
  const [dynamicSelectorOptions, setDynamicSelectorOptions] = useState([])
  const [dynamicSelectorLabel, setDynamicSelectorLabel] = useState('')
  const [dynamicSelectorInputValue, setDynamicSelectorInputValue] = useState('')
  const [submissionFormat, setSubmissionFormat] = useState(null)
  const [manualEventTriggered, setManualEventTriggered] = useState(false)
  const [isMounted, setIsMounted] = useState(false)

  // ----------------------------------------
  // HELPERS
  // ----------------------------------------
  const exportIsEnabled = useCallback((format) => exportOptions[format] === true, [exportOptions])

  const findFieldByName = useCallback((name) => fields.find((field) => field.name === name), [fields])

  const getFieldOperator = useCallback((field) => {
    const operators = { eq: 'eq', cont: 'in', in: 'in', gt: 'gt', lt: 'lt', gte: 'gte', lte: 'lte' }

    if (!field?.name) {
      return operators.eq
    }

    const suffix = field.name.split('_').pop()
    const operatorSuffix = operators[suffix] || operators.eq
    return field.name.split('_').includes('not') ? `"not ${operatorSuffix}"` : `"${operatorSuffix}"`
  })

  // ----------------------------------------
  // HANDLERS
  // ----------------------------------------
  const openFieldSelector = useCallback(() => {
    setCurrentField(null)
    setFieldSelectorFocus(true)
  })

  const handleFieldSelectorChange = useCallback(
    (selected) => {
      const field = findFieldByName(selected.value)
      setCurrentField({ ...field, value: selected.value })
      setDynamicSelectorLabel(`${field.label} ${getFieldOperator(field)}`)
      setDynamicSelectorOptions(field?.options || [])
      setDynamicSelectorInputValue('')
    },
    [findFieldByName, getFieldOperator],
  )

  const setQueryItem = useCallback(
    (value) => {
      setManualEventTriggered(true)

      if (currentField?.multiple) {
        const currentValues = query[currentField.value] || []
        value = [...currentValues, value]
      }

      const newQuery = { ...query, [currentField.value]: value }
      setQuery(newQuery)
      setDynamicSelectorInputValue('')
      setCurrentField(null)
      setFieldSelectorFocus(false)
    },
    [query, currentField],
  )

  const deleteQueryItem = useCallback(
    (field, value) => {
      setManualEventTriggered(true) // flag to indicate that the user has done an interaction

      const deleteItem = (key) => {
        const newQuery = { ...query }
        delete newQuery[key]
        setQuery(newQuery)
      }

      if (field?.default) {
        delete field.default
      }

      if (field?.multiple) {
        const currentValues = query[field.name] || []
        const newValues = currentValues.filter((v) => v !== value)
        if (newValues.length > 0) {
          setQuery({ ...query, [field.name]: newValues })
        } else {
          deleteItem(field.name)
        }
      } else {
        deleteItem(field.name)
      }
    },
    [query],
  )

  const handleDynamicSelectorChange = useCallback(
    (selected) => {
      setQueryItem(selected.value)
    },
    [setQueryItem],
  )

  const handleDynamicSelectorInputChange = useCallback((inputValue) => {
    setDynamicSelectorInputValue(inputValue)
  }, [])

  const handleDynamicSelectorKeyDown = useCallback(
    (event) => {
      if (dynamicSelectorOptions.length > 0) {
        return
      }

      if (event.key === 'Enter') {
        setQueryItem(dynamicSelectorInputValue)
      }
    },
    [dynamicSelectorOptions, setQueryItem, dynamicSelectorInputValue],
  )

  const clearQuery = useCallback(() => {
    React.startTransition(() => {
      setManualEventTriggered(true)
      setSort({ sort: null, dir: 'asc' })
      setPage(1)
      setQuery({})
    })
  }, [])

  const clearSort = useCallback(() => {
    React.startTransition(() => {
      setManualEventTriggered(true)
      setSort({ sort: null, dir: 'asc' })
    })
  }, [])

  const clearPage = useCallback(() => {
    React.startTransition(() => {
      setManualEventTriggered(true)
      setPage(1)
    })
  }, [])

  // ----------------------------------------
  // EFFECTS
  // ----------------------------------------
  useEffect(() => {
    setIsMounted(true)
  }, [])

  // This effect triggers the onChange callback when query, sort or page changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: cannot include `onChange` as it change on every render
  useEffect(() => {
    // await user interaction before submitting
    if (!isMounted || !manualEventTriggered) {
      return
    }

    onChange(query, sort, page)
  }, [query, sort, page, isMounted, manualEventTriggered])

  // This effect updates the page state when the pageOptions prop changes
  useEffect(() => {
    setPage(initialPaging.page || 1)
  }, [initialPaging.page])

  // This effect updates the sort state when the sortOptions prop changes
  useEffect(() => {
    setSort(initialSorting)
  }, [initialSorting])

  // This effect is used to trigger onChange callback with submission format (e.g. 'csv').
  // It only works for scenarios where this component is rendered inside the form (by the dynamic_search_controller)
  // biome-ignore lint/correctness/useExhaustiveDependencies: cannot include `onChange` as it change on every render
  useEffect(() => {
    if (submissionFormat) {
      onChange(submissionFormat)
      setSubmissionFormat(null)
    }
  }, [submissionFormat])

  // ----------------------------------------
  // RENDERERS
  // ----------------------------------------
  const renderBadge = useCallback(
    (index, field, operator, valueLabel, value) => {
      return (
        <span key={index} className='badge border bg-lightblue text-blue'>
          <span>
            {field.label} {operator} {valueLabel || value}
          </span>
          <button
            type='button'
            className='btn-close'
            aria-label={t`Close`}
            onClick={() => deleteQueryItem(field, value)}
          />
        </span>
      )
    },
    [deleteQueryItem],
  )

  return (
    <div className='dynamic-search'>
      <div className='dynamic-search-autocomplete' data-testid='dynamic-search-autocomplete'>
        {!currentField && (
          <Select
            classNamePrefix='field-selector'
            inputId='field-selector'
            placeholder={t`Select a field for searching`}
            className='form-control'
            options={fieldSelectorOptions}
            onChange={handleFieldSelectorChange}
            onSelect={handleFieldSelectorChange}
            isClearable
            autoFocus={fieldSelectorFocus}
            openMenuOnFocus={fieldSelectorFocus}
          />
        )}

        {currentField && (
          <div className='input-group'>
            <div className='input-group-prepend'>
              <button type='button' className='input-group-text' onClick={openFieldSelector}>
                {dynamicSelectorLabel}
              </button>
            </div>

            <Select
              classNamePrefix='value-selector'
              inputId='value-selector'
              components={
                {
                  date: { Input: DateInput },
                  datetime: { Input: DateTimeInput },
                  text: {},
                }[currentField.type || 'text']
              }
              placeholder={
                {
                  date: 'yyyy-mm-dd',
                  datetime: 'yyyy-mm-dd hh:mm',
                  text: 'Enter a value for filtering',
                }[currentField.type || 'text']
              }
              className='form-control'
              options={dynamicSelectorOptions}
              inputValue={dynamicSelectorInputValue}
              onInputChange={handleDynamicSelectorInputChange}
              onChange={handleDynamicSelectorChange}
              onSelect={handleDynamicSelectorChange}
              onKeyDown={handleDynamicSelectorKeyDown}
              noOptionsMessage={() => {
                return dynamicSelectorOptions.length > 0
                  ? t`No options found`
                  : t`Press Enter to set this value for filtering`
              }}
              autoFocus
              openMenuOnFocus={dynamicSelectorOptions.length > 0}
            />
          </div>
        )}
      </div>

      <div className='dynamic-search-query'>
        {Object.keys(query).map((key, index) => {
          const field = findFieldByName(key)
          const operator = getFieldOperator(field)
          const value = query[key]
          const valueLabel = field?.options?.find((opt) => opt.value === value)?.label

          if (field?.multiple) {
            return value.map((value, index) => {
              const valueLabel = field?.options?.find((opt) => opt.value === value)?.label
              return renderBadge(index, field, operator, valueLabel, value)
            })
          }

          return renderBadge(index, field, operator, valueLabel, value)
        })}

        {sort.sort && (
          <span key='sort' className='badge border bg-lightblue text-blue'>
            <span>
              {t`Sort:`} {sort.sortLabel} {sort.dir.toUpperCase()}
            </span>
            <button type='button' className='btn-close' aria-label={t`Close`} onClick={clearSort} />
          </span>
        )}

        {page && page > 1 && (
          <span key='page' className='badge border bg-lightblue text-blue'>
            <span>
              {t`Page:`} {page}
            </span>
            <button type='button' className='btn-close' aria-label={t`Close`} onClick={clearPage} />
          </span>
        )}

        {(Object.keys(query).length > 0 || sort.sort || (page && page > 1)) && (
          <button type='button' className='btn btn-primary btn-sm' onClick={(_e) => clearQuery()}>
            <Trans>Clear filters</Trans>
          </button>
        )}
      </div>

      <div className='dynamic-search-inputs'>
        {Object.keys(query).map((key, index) => {
          const field = findFieldByName(key)

          if (field?.multiple) {
            return query[key].map((value, index) => {
              // biome-ignore lint/suspicious/noArrayIndexKey: todo: fix this
              return <input key={index} type='hidden' name={`query[${key}][]`} value={value} />
            })
          } else {
            // biome-ignore lint/suspicious/noArrayIndexKey: todo: fix this
            return <input key={index} type='hidden' name={`query[${key}]`} value={query[key]} />
          }
        })}

        <input type='hidden' name='sort' value={sortOptions.sort ?? ''} />
        <input type='hidden' name='dir' value={sortOptions.dir ?? ''} />
        <input type='hidden' name='remember_filters' value='true' />
      </div>

      <div className='dynamic-search-actions'>
        {exportIsEnabled('csv') && (
          <button type='button' className='btn btn-secondary btn-sm' onClick={() => setSubmissionFormat('csv')}>
            <Trans>Export CSV</Trans>
          </button>
        )}

        {submissionFormat && <input type='hidden' name='format' value={submissionFormat} />}
      </div>
    </div>
  )
}
